/// What the panel lets through as a company logo, before anything is sent.
/// The server checks the same rules again by the file's bytes
/// (services/orgs/src/utils/logo-storage.ts) — this is so the person hears
/// «too big» in Ukrainian at once, not after a 2 MB upload.

/// Mirrors MAX_LOGO_BYTES in services/orgs.
export const MAX_LOGO_BYTES = 2 * 1024 * 1024

export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp'

const ALLOWED_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/webp'])
const ALLOWED_EXTENSIONS = /\.(png|jpe?g|webp)$/i

export type LogoProblem = 'type' | 'size' | 'empty'

export type LogoCheck = Readonly<{ ok: true }> | Readonly<{ ok: false; problem: LogoProblem }>

/// A file as the picker or a drop hands it over — only the parts we read.
export type LogoCandidate = Readonly<{ name: string; type: string; size: number }>

/// The browser's type decides; when it gives none (some drops do), the
/// extension. SVG is refused whatever it is called: the server never takes it.
/// The same rules for a logo and a staff photo (domain/staff.ts) — only the
/// size differs.
export const checkImageFile = (file: LogoCandidate, maxBytes: number): LogoCheck => {
  const type = file.type.toLowerCase()
  const known = type ? ALLOWED_TYPES.has(type) : ALLOWED_EXTENSIONS.test(file.name)
  if (!known) return { ok: false, problem: 'type' }
  if (file.size === 0) return { ok: false, problem: 'empty' }
  if (file.size > maxBytes) return { ok: false, problem: 'size' }
  return { ok: true }
}

export const checkLogoFile = (file: LogoCandidate): LogoCheck => checkImageFile(file, MAX_LOGO_BYTES)

/// «Т» for «Тренажерний», «АС» for «Атлет Спорт» — what the monogram tile
/// shows when there is no logo. Guillemets and quotes are skipped, so
/// «Атлет» reads as «А», not ««».
export const monogramOf = (name: string): string => {
  const words = name
    .replace(/[«»"'„“”()[\]]/g, ' ')
    .split(/\s+/)
    .filter((word) => /[\p{L}\p{N}]/u.test(word))
  const letters = words
    .slice(0, 2)
    .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0]?.toLocaleUpperCase('uk') ?? '')
    .join('')
  return letters || '·'
}
